/**
 * Streak / weekly-calendar helpers.
 *
 * Web port of the iOS streak math:
 *   - client-native/src/features/home/components/WeeklyCalendar.tsx (week strip, Mon-first)
 *   - client-native/src/features/home/components/StreakCalendarDisplay.tsx (computeWeekDays)
 *   - client-native/src/features/home/components/StreakCard.tsx (rolling-from-today card)
 *
 * The server only returns `dayStreak` (consecutive days incl. today) + today's
 * date, so the per-day completed/empty state is derived here exactly like iOS.
 */

export type DayState = "today" | "completed" | "empty" | "future";

export interface WeekDay {
  /** Single-letter label for the home strip (Mon-first): M T W T F S S. */
  label: string;
  /** Two-letter label for the streak card (rolling from today): Fr Sa Su... */
  shortLabel: string;
  /** Day-of-month number (1..31). */
  dayNumber: number;
  state: DayState;
  /** True for the column representing today. */
  isToday: boolean;
}

const ONE_LETTER = ["S", "M", "T", "W", "T", "F", "S"]; // Sun..Sat
const TWO_LETTER = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]; // Sun..Sat

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86_400_000);
}

/** A single day's logged activity (iOS weeklyListeningData entry). */
export interface DayActivity {
  /** Local date key, YYYY-MM-DD. */
  date: string;
  value: number;
}

function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Home hero week strip — a fixed Monday-first calendar week containing `today`.
 * A past day is `completed` if it had logged activity OR falls inside the active
 * streak (iOS computeWeekDays: `completed = hasActivity || isInStreak`); past
 * days with neither are `empty`; future days are `future`; today is `today`.
 */
export function computeHomeWeek(
  dayStreak: number,
  now: Date = new Date(),
  weeklyData: DayActivity[] = [],
): WeekDay[] {
  const today = startOfDay(now);
  // Monday-first: getDay() is 0(Sun)..6(Sat); offset to Monday.
  const mondayOffset = (today.getDay() + 6) % 7;
  const monday = addDays(today, -mondayOffset);

  // The streak covers [today - (dayStreak-1) .. today].
  const streakStart = addDays(today, -(Math.max(0, dayStreak) - 1));

  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(monday, i);
    const diffFromToday = daysBetween(date, today); // >0 past, <0 future
    const isToday = diffFromToday === 0;
    const hasActivity =
      (weeklyData.find((d) => d.date === localDateKey(date))?.value ?? 0) > 0;
    const isInStreak =
      dayStreak > 0 && date.getTime() >= streakStart.getTime();
    let state: DayState;
    if (isToday) {
      state = "today";
    } else if (diffFromToday < 0) {
      state = "future";
    } else if (hasActivity || isInStreak) {
      state = "completed";
    } else {
      state = "empty";
    }
    return {
      label: ONE_LETTER[date.getDay()],
      shortLabel: TWO_LETTER[date.getDay()],
      dayNumber: date.getDate(),
      state,
      isToday,
    };
  });
}

/** One cell in the expanded month grid. `dayNumber` is null for leading/trailing
 *  blanks. iOS source: StreakCalendarDisplay.tsx MonthDayInfo (:25-32). */
export interface MonthDay {
  /** Day-of-month (1..31), or null for padding cells. */
  dayNumber: number | null;
  isToday: boolean;
  isFuture: boolean;
  completed: boolean;
  isJoinDate: boolean;
  isBeforeJoin: boolean;
}

const EMPTY_MONTH_DAY: MonthDay = {
  dayNumber: null,
  isToday: false,
  isFuture: false,
  completed: false,
  isJoinDate: false,
  isBeforeJoin: false,
};

/**
 * Sunday-first month grid for `viewDate`'s month, as rows of 7 cells (iOS
 * computeMonthWeeks, StreakCalendarDisplay.tsx:85-142). `today` drives
 * isToday/isFuture/streak; `joinDate` (optional) flags the join cell and dims
 * days before it. A past day is `completed` if it had logged activity OR falls
 * inside the active streak (and is on/after the join date).
 */
export function computeMonthWeeks(
  viewDate: Date,
  today: Date,
  dayStreak: number,
  weeklyData: DayActivity[] = [],
  joinDate?: Date,
): MonthDay[][] {
  const todayKey = localDateKey(startOfDay(today));
  const joinKey = joinDate ? localDateKey(startOfDay(joinDate)) : null;
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay(); // 0 = Sunday
  const totalDays = lastDay.getDate();
  const joinTime = joinDate ? startOfDay(joinDate).getTime() : null;

  const weeks: MonthDay[][] = [];
  let week: MonthDay[] = [];

  for (let i = 0; i < startDow; i++) week.push(EMPTY_MONTH_DAY);

  for (let day = 1; day <= totalDays; day++) {
    const d = new Date(year, month, day);
    const key = localDateKey(d);
    const isToday = key === todayKey;
    const isJoinDate = key === joinKey;
    const isBeforeJoin =
      joinTime !== null ? d.getTime() < joinTime && !isJoinDate : false;
    const diffFromToday = daysBetween(d, today); // >0 past, <0 future
    const isFuture = diffFromToday < 0;
    const hasActivity =
      (weeklyData.find((wd) => wd.date === key)?.value ?? 0) > 0;
    const isInStreak =
      !isFuture &&
      !isBeforeJoin &&
      dayStreak > 0 &&
      diffFromToday >= 0 &&
      diffFromToday < dayStreak;

    week.push({
      dayNumber: day,
      isToday,
      isFuture,
      completed: hasActivity || isInStreak,
      isJoinDate,
      isBeforeJoin,
    });
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(EMPTY_MONTH_DAY);
    weeks.push(week);
  }
  return weeks;
}
